<template>
  <!-- SIGN UP / USERNAME -->
  <!-- ------------------ -->

  <div :class="$style.toggleTitle">
    <!-- LOGGED OUT STATE -->
    <template v-if="!user">
      <NsIcon
        :class="$style.profileIcon"
        icon-name="icon-profile"
        size="custom"
      />
      <span
        :class="compact ? $style.signUpCompact : $style.signUp"
        @click="signUp"
        >{{ signUpLabel }}</span
      >
      <span :class="compact ? $style.slashCompact : $style.slash">/</span>
      <span
        :class="compact ? $style.signInCompact : $style.signIn"
        @click="signIn"
        >{{ signInLabel }}</span
      >
    </template>

    <!-- LOGGED IN STATE -->
    <template v-else>
      <button
        v-if="!compact"
        :class="$style.maxWidth"
        @click="redirectToProfile"
      >
        <NsIcon
          :class="$style.profileIcon"
          icon-name="icon-profile"
          size="custom"
        />
      </button>
      <div
        :class="compact ? $style.greetingCompact : $style.greeting"
        v-html="greetingText"
      ></div>
      <span v-if="!compact" :class="$style.slash">/</span>
      <button
        v-if="!compact"
        :href="signOutUrl"
        :class="$style.maxWidth"
        @click="event => logout(event)"
      >
        {{ signOutLabel }}
      </button>
    </template>
  </div>
</template>

<script>
import { events, util } from "@nuskin/ns-util";
import { UserService, AccountManager } from "@nuskin/ns-account";
import { NsIcon } from "@nuskin/design-components";

import vueEventBus from "../EventBus";

export default {
  name: "NsMinimalHeaderProfileToggle",
  components: {
    NsIcon
  },
  props: {
    /**
     * Greeting template from translations
     */
    greeting: {
      type: String,
      default: "Hi %username%"
    },
    signInLabel: {
      type: String,
      default: ""
    },
    signUpLabel: {
      type: String,
      default: ""
    },
    signUpHref: {
      type: String,
      default: ""
    },
    signOutLabel: {
      type: String,
      default: "Sign Out"
    },
    /**
    /**
     * The url where signout link takes you
     */
    signOutUrl: {
      type: String,
      default: ""
    },
    profileUrl: {
      type: String,
      default: ""
    },
    /**
     * Compact mode. Hide buttons
     */
    compact: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    user: null
  }),
  computed: {
    desktop() {
      return this.$mq === "desktop" || this.$mq === "desktopHD";
    },
    greetingText() {
      let displayName = this.user.getDisplayName();
      return this.greeting
        .replace("<br>", " ")
        .replace("%username%", displayName);
    }
  },
  mounted() {
    this.addListeners();
    this.setUser();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    setUser() {
      const isLoggedIn = UserService.isLoggedIn();
      if (!isLoggedIn) {
        this.user = null;
        return;
      }

      this.user = UserService.getUser();
    },

    logout(event) {
      event.preventDefault();
      const signOutUrl =
        this.signOutUrl === "#"
          ? util.getLocaleUrl() + "home.html"
          : this.signOutUrl;
      AccountManager.afterLogoutRedirect = signOutUrl;
      AccountManager.logout();
    },

    signUp() {
      window.location.href = this.signUpHref;
    },

    redirectToProfile() {
      window.location.href = this.profileUrl;
    },

    signIn() {
      events.publish(events.authentication.REQUEST_FOR_LOGIN, {
        href: window.location.href
      });
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      // listen to config changes
      vueEventBus.$on("config-changed", this.setUser);

      // LOGIN EVENTS
      events.subscribe(events.authentication.LOGIN_COMPLETE, this.setUser);
      events.subscribe(events.authentication.LOGOUT, this.setUser);
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to event bus
      vueEventBus.$off();

      // LOGIN EVENTS
      events.unsubscribe(events.authentication.LOGIN_COMPLETE, this.setUser);
      events.unsubscribe(events.authentication.LOGOUT, this.setUser);
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';

* {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.container {
  @include global.subtitle-small-bold-caps;
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  margin: 0;
  padding-left: 15px;
  height: 60px;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.8px;
  color: map.get(colors.$themeColors, color-interactive-7);
  font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
  text-align: left;
  outline: none;
  cursor: pointer;
}

.toggleTitle {
  flex: 1;
  display: flex;
  align-items: center;
  white-space: nowrap;
  font-weight: 400;
}

.slash,
.slashCompact {
  padding: 0 5px;
}

.profileImage {
  border: 1px solid map.get(colors.$themeColors, color-interactive-8);
  border-radius: 50%;
  height: 26px;
  width: 26px;
  overflow: hidden;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  z-index: 1;
}

// nested for specificity
.toggleTitle .profileIcon {
  width: 26px;
  height: 26px;
  margin-left: 30px;
}

.profileIcon {
  margin-right: 5px;
}

.signUp,
.signUpCompact,
.signIn,
.signInCompact {
  cursor: pointer;
}

.greeting {
  flex: 1;
  line-height: 18px;
}

.greetingCompact {
  padding-left: 5px;
}

.toggleIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  cursor: pointer;
}

.arrowIcon {
  transition: map.get(other.$otherUtils, global-transition);

  &.open {
    transform: scaleY(-1);
  }
}

/** ************************* */
/** DESKTOP STYLE ADJUSTMENTS */
/** ************************* */

.container.desktop {
  padding: 0;
  height: auto;

  .profileImage {
    width: 20px;
    height: 20px;
  }

  .profileIcon {
    width: 20px;
    height: 20px;
  }

  .greeting {
    padding: 0 5px;

    br {
      display: none;
    }
  }

  .toggleIcon {
    margin-left: 10px;
    width: 14px;
    height: 14px;
  }

  .arrowIcon {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 768px) {
  .greeting,
  .maxWidth,
  .signUp,
  .slash,
  .signIn,
  .compact,
  .slashCompact,
  .signUpCompact,
  .signInCompact {
    display: none;
  }
}
</style>
